import { Check, Clock, XCircle, PenTool } from 'lucide-react';
import type { Block } from '@/types';

interface SignatureBlockContent {
  label?: string;
  signatureData?: string;
  signerName?: string;
  signedAt?: string;
  required?: boolean;
}

interface SignatureBlockProps {
  block: Block;
  isEditable?: boolean;
  onUpdate?: (patch: Partial<Block>) => void;
  boxSignStatus?: string | null;
  boxSignedAt?: string | null;
}

function SignatureBlock({ block, isEditable = false, boxSignStatus, boxSignedAt }: SignatureBlockProps) {
  const content = block.content as SignatureBlockContent;
  const label = content.label || 'Handtekening';

  const isSigned = boxSignStatus === 'signed';
  const isDeclined = boxSignStatus === 'declined';
  const isWaiting = boxSignStatus === 'sent' || boxSignStatus === 'viewed' || boxSignStatus === 'created';

  return (
    <div className="rounded-lg border p-4">
      <p className="text-sm text-muted-foreground mb-2">{label}</p>

      {isEditable && (
        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-700 flex items-center gap-2 mb-3">
          <PenTool className="h-4 w-4 shrink-0" />
          Ondertekening verloopt via Box Sign
        </div>
      )}

      <div
        className="h-[80px] flex items-center justify-center rounded-md"
        style={{
          border: `2px solid ${isSigned ? '#16a34a' : isDeclined ? '#dc2626' : isWaiting ? '#2563eb' : '#d1d5db'}`,
          backgroundColor: isSigned ? '#f0fdf4' : isDeclined ? '#fef2f2' : isWaiting ? '#eff6ff' : '#f9fafb',
        }}
      >
        {isSigned ? (
          <div className="flex items-center gap-2 text-green-700">
            <Check className="h-5 w-5" />
            <div>
              <p className="text-sm font-medium">Ondertekend</p>
              {boxSignedAt && (
                <p className="text-xs opacity-75">
                  {new Date(boxSignedAt).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </p>
              )}
            </div>
          </div>
        ) : isDeclined ? (
          <div className="flex items-center gap-2 text-red-700">
            <XCircle className="h-5 w-5" />
            <p className="text-sm font-medium">Afgewezen</p>
          </div>
        ) : isWaiting ? (
          <div className="flex items-center gap-2 text-blue-700">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
            </span>
            <p className="text-sm font-medium">Wacht op ondertekening</p>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-500">
            <Clock className="h-5 w-5" />
            <p className="text-sm font-medium">Wacht op ondertekening</p>
          </div>
        )}
      </div>

      {content.required && !isSigned && (
        <p className="text-xs text-destructive mt-2">* Verplicht</p>
      )}
    </div>
  );
}

export default SignatureBlock;
